import { AdMob, MaxAdContentRating } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

export type AdOutcome = 'completed' | 'dismissed' | 'failed' | 'preview';
export type AdMode = 'android-native' | 'web-preview';

const TEST_REWARDED_ID = 'ca-app-pub-3940256099942544/5224354917';
const TEST_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/1033173712';
const INTERSTITIAL_COOLDOWN_MS = 3 * 60 * 1000;
const INTERSTITIAL_KEY = 'mallang-note:last-interstitial-at';
const RACE_REPORT_UNLOCK_KEY = 'mallang-note:unlocked-race-reports:v1';
const LEGACY_REPORT_UNLOCK_KEY = 'mallang-note:unlocked-reports';

const isTestMode = import.meta.env.VITE_ADMOB_TEST_MODE !== 'false';
const rewardedId = import.meta.env.VITE_ADMOB_REWARDED_ID || TEST_REWARDED_ID;
const interstitialId = import.meta.env.VITE_ADMOB_INTERSTITIAL_ID || TEST_INTERSTITIAL_ID;

export const adMode: AdMode = Capacitor.getPlatform() === 'android' ? 'android-native' : 'web-preview';

let initialized = false;

export const initializeAds = async (): Promise<void> => {
  if (initialized || adMode !== 'android-native') return;

  await AdMob.initialize({
    initializeForTesting: isTestMode,
    maxAdContentRating: MaxAdContentRating.General,
    tagForChildDirectedTreatment: false,
    tagForUnderAgeOfConsent: false,
  });
  initialized = true;
};

export const showRewardedAd = async (): Promise<AdOutcome> => {
  if (adMode === 'web-preview') return 'preview';

  try {
    await initializeAds();
    await AdMob.prepareRewardVideoAd({
      adId: rewardedId,
      isTesting: isTestMode,
      npa: true,
      immersiveMode: false,
    });
    const reward = await AdMob.showRewardVideoAd();
    return reward.amount > 0 ? 'completed' : 'dismissed';
  } catch {
    return 'failed';
  }
};

export const canShowInterstitial = (now = Date.now()): boolean => {
  const previous = Number(localStorage.getItem(INTERSTITIAL_KEY) ?? 0);
  return !Number.isFinite(previous) || previous === 0 || now - previous >= INTERSTITIAL_COOLDOWN_MS;
};

export const showInterstitialAd = async (now = Date.now()): Promise<AdOutcome> => {
  if (!canShowInterstitial(now)) return 'dismissed';
  if (adMode === 'web-preview') {
    localStorage.setItem(INTERSTITIAL_KEY, String(now));
    return 'preview';
  }

  try {
    await initializeAds();
    await AdMob.prepareInterstitial({
      adId: interstitialId,
      isTesting: isTestMode,
      npa: true,
      immersiveMode: false,
    });
    await AdMob.showInterstitial();
    localStorage.setItem(INTERSTITIAL_KEY, String(now));
    return 'completed';
  } catch {
    return 'failed';
  }
};

const readStringArray = (value: string | null): string[] => {
  try {
    const parsed = JSON.parse(value ?? '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const raceIdFromLegacyReportId = (reportId: string): string | null => {
  const matched = reportId.match(/^report-(.+)-\d{14}$/);
  return matched?.[1] ?? null;
};

const readUnlockedRaceReports = (): string[] => {
  const unlocked = new Set(readStringArray(localStorage.getItem(RACE_REPORT_UNLOCK_KEY)));
  for (const reportId of readStringArray(sessionStorage.getItem(LEGACY_REPORT_UNLOCK_KEY))) {
    const raceId = raceIdFromLegacyReportId(reportId);
    if (raceId) unlocked.add(raceId);
  }
  const values = [...unlocked];
  localStorage.setItem(RACE_REPORT_UNLOCK_KEY, JSON.stringify(values));
  return values;
};

export const isRaceReportUnlocked = (raceId: string): boolean =>
  readUnlockedRaceReports().includes(raceId);

export const unlockRaceReport = (raceId: string): void => {
  const unlocked = new Set(readUnlockedRaceReports());
  unlocked.add(raceId);
  localStorage.setItem(RACE_REPORT_UNLOCK_KEY, JSON.stringify([...unlocked]));
};
